const PLATFORM = "practice_studio" as const;
const CONSENT_KEY = "varai.analytics.consent.v1";
const PENDING_KEY = "varai.ga4.confirmed.pending.v1";
const SENT_KEY = "varai.ga4.confirmed.sent.v1";
const SCRIPT_ID = "varai-ga4-script";

export type SignUpMethod = "email" | "invite" | "oauth" | "sso";
export type SignUpLocation =
  | "homepage_nav"
  | "homepage_hero"
  | "homepage_footer"
  | "login"
  | "verification_retry";

export interface AnalyticsEventParams {
  sign_up_cta_click: { location: SignUpLocation };
  sign_up_view: Record<string, never>;
  sign_up_start: Record<string, never>;
  sign_up_account_type: {
    account_type: "practice" | "individual" | "enterprise";
  };
  sign_up_submit: Record<string, never>;
  sign_up: { method: SignUpMethod };
  verification_email_sent: Record<string, never>;
  email_verified: Record<string, never>;
  first_login: { method: SignUpMethod };
  sign_up_error: { error_code: string };
  invite_accepted: Record<string, never>;
  first_data_import_completed: Record<string, never>;
  first_order_created: Record<string, never>;
}

export type AnalyticsEventName = keyof AnalyticsEventParams;
export type ConfirmedAnalyticsEvent =
  | "sign_up"
  | "verification_email_sent"
  | "email_verified"
  | "first_login"
  | "invite_accepted"
  | "first_data_import_completed"
  | "first_order_created";

interface QueuedEvent<
  E extends ConfirmedAnalyticsEvent = ConfirmedAnalyticsEvent,
> {
  event: E;
  params: AnalyticsEventParams[E];
}

declare global {
  interface Window {
    dataLayer?: unknown[][];
    gtag?: (...args: unknown[]) => void;
    VARAI_GA4_MEASUREMENT_ID?: string;
    varaiGa4?: { prodHosts: string[]; isProductionHost: (host: string) => boolean };
  }
}

const PARAM_KEYS: {
  [E in AnalyticsEventName]: ReadonlyArray<keyof AnalyticsEventParams[E]>;
} = {
  sign_up_cta_click: ["location"],
  sign_up_view: [],
  sign_up_start: [],
  sign_up_account_type: ["account_type"],
  sign_up_submit: [],
  sign_up: ["method"],
  verification_email_sent: [],
  email_verified: [],
  first_login: ["method"],
  sign_up_error: ["error_code"],
  invite_accepted: [],
  first_data_import_completed: [],
  first_order_created: [],
};

const LOCATIONS: ReadonlyArray<SignUpLocation> = [
  "homepage_nav",
  "homepage_hero",
  "homepage_footer",
  "login",
  "verification_retry",
];
const METHODS: ReadonlyArray<SignUpMethod> = [
  "email",
  "invite",
  "oauth",
  "sso",
];
const ACCOUNT_TYPES = ["practice", "individual", "enterprise"] as const;
const CONFIRMED_EVENTS: ReadonlyArray<ConfirmedAnalyticsEvent> = [
  "sign_up",
  "verification_email_sent",
  "email_verified",
  "first_login",
  "invite_accepted",
  "first_data_import_completed",
  "first_order_created",
];
const PII_KEY =
  /(^|_)(email|name|company|store|user_?id|phone|address|token)($|_)/i;
const EMAIL_VALUE = /[^\s@]+@[^\s@]+\.[^\s@]+/;
const UUID_VALUE =
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/i;

function hasPii(params: Record<string, unknown>): boolean {
  return Object.entries(params).some(
    ([key, value]) =>
      PII_KEY.test(key) ||
      (typeof value === "string" &&
        (EMAIL_VALUE.test(value) || UUID_VALUE.test(value))),
  );
}

export function isAnalyticsPayloadSafe(
  params: Record<string, unknown>,
): boolean {
  return !hasPii(params);
}

export type AnalyticsConsent = "granted" | "denied";

export function getAnalyticsConsent(): AnalyticsConsent | null {
  const stored = window.localStorage.getItem(CONSENT_KEY);
  return stored === "granted" || stored === "denied" ? stored : null;
}

/**
 * Delegates to the allow-list in public/ga-init.js (the single source, exposed
 * as window.varaiGa4). Fails closed if that script did not load.
 */
export function isProductionHost(
  hostname: string = typeof window !== "undefined" ? window.location.hostname : "",
): boolean {
  return window.varaiGa4?.isProductionHost(hostname) === true;
}

export function initializeAnalytics(
  hostname: string = window.location.hostname,
): boolean {
  if (!isProductionHost(hostname)) return false;
  if (getAnalyticsConsent() !== "granted") return false;
  const measurementId = window.VARAI_GA4_MEASUREMENT_ID;
  if (!measurementId || typeof window.gtag !== "function") return false;

  window.gtag("consent", "update", { analytics_storage: "granted" });
  if (!document.getElementById(SCRIPT_ID)) {
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    document.head.appendChild(script);
    window.gtag("js", new Date());
    window.gtag("config", measurementId, {
      site: "retail",
    });
  }
  return true;
}

export function setAnalyticsConsent(
  consent: AnalyticsConsent,
  hostname: string = window.location.hostname,
): void {
  window.localStorage.setItem(CONSENT_KEY, consent);
  if (consent === "granted") {
    initializeAnalytics(hostname);
    flushConfirmedAnalyticsEvents();
  } else {
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", { analytics_storage: "denied" });
    }
    window.localStorage.removeItem(PENDING_KEY);
    window.localStorage.removeItem(SENT_KEY);
  }
}

function hasValidParams<E extends AnalyticsEventName>(
  event: E,
  params: Record<string, unknown>,
): params is AnalyticsEventParams[E] {
  const expectedKeys = PARAM_KEYS[event] as string[];
  const actualKeys = Object.keys(params);
  if (
    !isAnalyticsPayloadSafe(params) ||
    actualKeys.length !== expectedKeys.length ||
    actualKeys.some((key) => !expectedKeys.includes(key))
  ) {
    return false;
  }

  if (event === "sign_up_cta_click")
    return LOCATIONS.includes(params.location as SignUpLocation);
  if (event === "sign_up" || event === "first_login") {
    return METHODS.includes(params.method as SignUpMethod);
  }
  if (event === "sign_up_account_type") {
    return ACCOUNT_TYPES.includes(
      params.account_type as (typeof ACCOUNT_TYPES)[number],
    );
  }
  if (event === "sign_up_error") {
    return (
      typeof params.error_code === "string" &&
      /^[a-z][a-z0-9_]{0,39}$/.test(params.error_code)
    );
  }
  return true;
}

function readStored<T>(key: string, fallback: T): T {
  try {
    const value = window.localStorage.getItem(key);
    return value ? (JSON.parse(value) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeStored(key: string, value: unknown): boolean {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

function readPendingEvents(): QueuedEvent[] {
  const stored = readStored<unknown>(PENDING_KEY, []);
  if (!Array.isArray(stored)) return [];

  return stored.filter((item): item is QueuedEvent => {
    if (!item || typeof item !== "object") return false;
    const { event, params } = item as { event?: unknown; params?: unknown };
    return (
      typeof event === "string" &&
      CONFIRMED_EVENTS.includes(event as ConfirmedAnalyticsEvent) &&
      !!params &&
      typeof params === "object" &&
      hasValidParams(
        event as ConfirmedAnalyticsEvent,
        params as Record<string, unknown>,
      )
    );
  });
}

function readSentEvents(): ConfirmedAnalyticsEvent[] {
  const stored = readStored<unknown>(SENT_KEY, []);
  if (!Array.isArray(stored)) return [];
  return stored.filter(
    (event): event is ConfirmedAnalyticsEvent =>
      typeof event === "string" &&
      CONFIRMED_EVENTS.includes(event as ConfirmedAnalyticsEvent),
  );
}

export function trackAnalyticsEvent<E extends AnalyticsEventName>(
  event: E,
  params: AnalyticsEventParams[E] = {} as AnalyticsEventParams[E],
): boolean {
  const untrustedParams = params as Record<string, unknown>;
  if (
    !hasValidParams(event, untrustedParams) ||
    getAnalyticsConsent() !== "granted" ||
    typeof window.gtag !== "function"
  )
    return false;

  window.gtag("event", event, { ...untrustedParams, platform: PLATFORM });
  return true;
}

export function queueConfirmedAnalyticsEvent<E extends ConfirmedAnalyticsEvent>(
  event: E,
  params: AnalyticsEventParams[E] = {} as AnalyticsEventParams[E],
): boolean {
  if (
    getAnalyticsConsent() !== "granted" ||
    !hasValidParams(event, params as Record<string, unknown>)
  )
    return false;

  const sent = readSentEvents();
  const pending = readPendingEvents();
  if (sent.includes(event) || pending.some((item) => item.event === event))
    return false;

  return writeStored(PENDING_KEY, [...pending, { event, params }]);
}

export function flushConfirmedAnalyticsEvents(): void {
  if (getAnalyticsConsent() !== "granted") return;

  const pending = readPendingEvents();
  const sent = new Set(readSentEvents());
  const remaining: QueuedEvent[] = [];

  for (const item of pending) {
    if (sent.has(item.event)) continue;
    if (trackAnalyticsEvent(item.event, item.params)) sent.add(item.event);
    else remaining.push(item);
  }

  writeStored(PENDING_KEY, remaining);
  writeStored(SENT_KEY, [...sent]);
}
